import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, ShieldCheck, Zap, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LogoWhite } from '@/components/Logo';
import { Seo } from '@/components/Seo';
import { PricingTiers } from '@/components/marketing/PricingTiers';
import { SeatCalculator } from '@/components/marketing/SeatCalculator';

const FAQ = [
  { q: 'Is there a free trial?', a: '7 days free, no credit card required. Cancel anytime.' },
  { q: 'How is pricing tiered?', a: 'Solo (1 seat) is $35/seat/month. Team (2–5 seats) drops to $30/seat. Scale (6+ seats) is just $25/seat — billing automatically tiers as you add team members.' },
  { q: 'What happens if I cancel?', a: 'You retain full access until the end of your billing period. Your data stays available for export for 30 days.' },
];

export default function PricingPage() {
  const offerSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'Real Leads CRM',
    description: 'Shared lead pipeline CRM with AI voice notes, email tracking, conversion analytics, and Seamless.AI sourcing. Tiered per-seat pricing: $35 solo, $30 team, $25 scale.',
    brand: { '@type': 'Brand', name: 'Real Leads CRM' },
    offers: [
      {
        '@type': 'Offer', name: 'Solo', price: '35', priceCurrency: 'USD',
        availability: 'https://schema.org/InStock', priceValidUntil: '2027-12-31',
        url: 'https://realleadscrm.com/pricing',
        priceSpecification: { '@type': 'UnitPriceSpecification', price: '35', priceCurrency: 'USD', unitText: 'MONTH' },
      },
      {
        '@type': 'Offer', name: 'Team (2–5 seats)', price: '30', priceCurrency: 'USD',
        availability: 'https://schema.org/InStock', priceValidUntil: '2027-12-31',
        url: 'https://realleadscrm.com/pricing',
        priceSpecification: { '@type': 'UnitPriceSpecification', price: '30', priceCurrency: 'USD', unitText: 'MONTH' },
      },
      {
        '@type': 'Offer', name: 'Scale (6+ seats)', price: '25', priceCurrency: 'USD',
        availability: 'https://schema.org/InStock', priceValidUntil: '2027-12-31',
        url: 'https://realleadscrm.com/pricing',
        priceSpecification: { '@type': 'UnitPriceSpecification', price: '25', priceCurrency: 'USD', unitText: 'MONTH' },
      },
    ],
    aggregateRating: { '@type': 'AggregateRating', ratingValue: '4.8', ratingCount: '120' },
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="pricing-page">
      <Seo
        title="Pricing — Real Leads CRM | Starting at $25/seat/month"
        description="Tiered per-seat pricing: $35 solo, $30 team (2–5 seats), $25 scale (6+ seats). Unlimited leads, AI voice notes, email tracking, Seamless.AI sourcing. 7-day free trial."
        path="/pricing"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(offerSchema)}</script>
      </Helmet>

      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="header-home-link">
            <LogoWhite size="sm" />
          </Link>
          <Link to="/login">
            <Button variant="ghost" className="text-zinc-300 hover:text-white hover:bg-white/5 rounded-full text-sm">Sign in</Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-20 pb-12 sm:pt-28 sm:pb-16 text-center px-6">
        <p className="text-xs font-bold tracking-[0.25em] uppercase text-zinc-500 mb-4">Pricing</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Pricing that <span className="text-[#007AFF]">grows with your team.</span>
        </h1>
        <p className="text-base sm:text-lg text-zinc-400 max-w-2xl mx-auto">
          Per-seat pricing that automatically tiers as you scale. Starting at $25/seat/month for teams of 6+.
        </p>
      </section>

      {/* Pricing tiers */}
      <section className="pb-20 px-6">
        <div className="mb-12">
          <SeatCalculator theme="dark" />
        </div>
        <PricingTiers theme="dark" />
      </section>

      {/* Value props row */}
      <section className="border-t border-white/10 py-16 px-6">
        <div className="max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-8">
          <ValueProp
            icon={<ShieldCheck className="w-5 h-5 text-[#007AFF]" />}
            title="Secure by default"
            body="TLS 1.3 in transit, encrypted at rest. Role-based access control + full audit log."
          />
          <ValueProp
            icon={<Zap className="w-5 h-5 text-[#007AFF]" />}
            title="Built for speed"
            body="Real-time updates via WebSockets. Offline-ready PWA. Push notifications on every device."
          />
          <ValueProp
            icon={<MessageSquare className="w-5 h-5 text-[#007AFF]" />}
            title="Real human support"
            body="AI chat for instant answers, with admin escalation built in. Priority email response."
          />
        </div>
      </section>

      {/* Mini FAQ */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-8 text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Common pricing questions
          </h2>
          <div className="space-y-4">
            {FAQ.map((item) => (
              <div key={item.q} className="border border-white/10 bg-white/[0.02] rounded-lg px-5 py-4" data-testid={`pricing-faq-${item.q.slice(0,10)}`}>
                <p className="text-sm font-medium text-white mb-1">{item.q}</p>
                <p className="text-sm text-zinc-400 leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link to="/#faq" className="text-sm text-zinc-500 hover:text-white underline-offset-4 hover:underline">
              See all FAQs →
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="border-t border-white/10 py-24 px-6 text-center">
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tighter mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Try it free for 7 days
        </h2>
        <p className="text-base text-zinc-400 mb-8 max-w-xl mx-auto">
          Set up takes 60 seconds. Cancel anytime, no questions asked.
        </p>
        <Link to="/register">
          <Button className="bg-[#007AFF] hover:bg-[#005bb5] text-white rounded-full h-12 px-8 font-semibold text-sm" data-testid="bottom-cta-button">
            Start free trial <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </Link>
      </section>

      <footer className="border-t border-white/10 py-8 text-center text-xs text-zinc-600">
        © {new Date().getFullYear()} Real Leads CRM. All rights reserved.
      </footer>
    </div>
  );
}

function ValueProp({ icon, title, body }) {
  return (
    <div className="text-left">
      <div className="w-9 h-9 rounded-full bg-[#007AFF]/10 border border-[#007AFF]/20 flex items-center justify-center mb-3">{icon}</div>
      <p className="text-base font-semibold text-white mb-1.5">{title}</p>
      <p className="text-sm text-zinc-400 leading-relaxed">{body}</p>
    </div>
  );
}
